import type { ReactNode, Ref } from 'react'
import { Radio as BaseRadio } from '@base-ui/react/radio'
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group'

import { cn } from '@gto/ui/cn'

import { radioVariants } from './radio.styles'

type RadioColor = 'primary' | 'default' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
type RadioSize = 'small' | 'medium' | 'large'
type RadioLabelPlacement = 'end' | 'start' | 'top' | 'bottom'

// Sentinel used as RadioGroup value when standalone radio is unchecked (controlled)
const NONE_SENTINEL = '__radio_none__'

type RadioOwnProps = {
  /** Whether the radio is checked — enables standalone controlled mode */
  checked?: boolean
  /** Initial checked state for standalone uncontrolled mode */
  defaultChecked?: boolean
  /** Callback fired when the checked state changes in standalone mode */
  onCheckedChange?: (checked: boolean) => void
  /** Value identifier used when inside a RadioGroup */
  value?: string
  /** Color variant */
  color?: RadioColor
  /** Size variant */
  size?: RadioSize
  /** Whether the radio is disabled */
  disabled?: boolean
  /** Whether the radio is required — shows an asterisk next to the label */
  required?: boolean
  /** Label displayed next to the radio */
  label?: ReactNode
  /** Position of the label relative to the radio */
  labelPlacement?: RadioLabelPlacement
  /** Form field name */
  name?: string
  /** Additional CSS classes for the root */
  className?: string
  /** React 19 ref prop */
  ref?: Ref<HTMLButtonElement>
  /** Ref for the hidden input element */
  inputRef?: Ref<HTMLInputElement>
}

type RadioProps = RadioOwnProps &
  Omit<React.HTMLAttributes<HTMLElement>, keyof RadioOwnProps | 'onChange'>

const Radio = ({
  checked,
  defaultChecked,
  onCheckedChange,
  value = '__radio__',
  color = 'primary',
  size = 'medium',
  disabled = false,
  required = false,
  label,
  labelPlacement = 'end',
  name,
  className,
  ref,
  inputRef,
  ...rest
}: RadioProps) => {
  const styles = radioVariants({ size, color, labelPlacement, disabled })
  const isStandalone = checked !== undefined || defaultChecked !== undefined

  const radioEl = (
    <BaseRadio.Root
      ref={ref}
      className={cn(styles.root(), label == null && className)}
      disabled={disabled}
      inputRef={inputRef}
      value={value}
      {...rest}
    >
      <BaseRadio.Indicator className={styles.indicator()} />
    </BaseRadio.Root>
  )

  const content =
    label == null ? (
      radioEl
    ) : (
      <label className={cn(styles.label(), className)}>
        {radioEl}
        <span className={styles.labelText()}>
          {label}
          {required && <span className="ml-1 text-error-main">*</span>}
        </span>
      </label>
    )

  if (isStandalone) {
    let groupValue: string | undefined
    if (checked === undefined) {
      groupValue = undefined
    } else {
      groupValue = checked ? value : NONE_SENTINEL
    }

    let defaultGroupValue: string | undefined
    if (defaultChecked === undefined || !defaultChecked) {
      defaultGroupValue = undefined
    } else {
      defaultGroupValue = value
    }

    return (
      <BaseRadioGroup
        defaultValue={defaultGroupValue}
        disabled={disabled}
        name={name}
        value={groupValue}
        onValueChange={(v) => onCheckedChange?.(v === value)}
      >
        {content}
      </BaseRadioGroup>
    )
  }

  return content
}

export { Radio }
export type { RadioColor, RadioLabelPlacement, RadioProps, RadioSize }
